import { Button } from '@cokitchen/cokitchen-components';
import { AddIcon } from 'cokitchen-icons';
import PageHeaderText from 'components/base/page-header-text';
import BlogCard from 'components/blog-card';
import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { apiService } from 'services/base.service';
import { ROUTE_KEYS } from 'utils/constants';
import { handleApiError } from 'utils/functions';
import InfiniteScroll from 'react-infinite-scroll-component';
import { BlogType } from 'models/blog';
import useDashboardContext from 'contexts/dashboard.context';
import AccessDenied from 'components/access-denied';

const Blogs = () => {
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const [blogs, setBlogs] = useState<BlogType[]>([]);
  const [careerPageStatus, setCareerPageStatus] = useSearchParams();
  const { permissions } = useDashboardContext();
  const filterType = careerPageStatus.get('status') || 'published';
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const { pathname } = useLocation();

  const goToCreateBlog = () => {
    navigate(ROUTE_KEYS.CREATE_BLOG);
  };

  const getBlogs = async () => {
    if (page === 1) setLoading(true);

    try {
      const res = await apiService.get('blog', {
        params: {
          is_draft: filterType === 'drafts',
          archived: filterType === 'archived',
          page: page
        }
      });

      setBlogs((prev) => [...prev, ...res.data.data]);
      setHasMore(page < Math.ceil(res.data.pagination.total / res.data.pagination.page_size));
    } catch (err) {
      handleApiError(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    setBlogs([]);
    setPage(1);
  }, [filterType, pathname]);

  useEffect(() => {
    getBlogs();
  }, [filterType, page]);

  const blogFilters = [
    {
      name: 'Published',
      value: 'published'
    },
    { name: 'Drafts', value: 'drafts' },
    { name: 'Archived', value: 'archived' }
  ];

  const fetchMoreData = () => {
    setPage((p) => p + 1);
  };

  const updateBlogs = (id: string) => {
    setBlogs(blogs.filter((blog) => blog.id !== id));
  };

  const canAccessBlog = permissions.canAccessBlog;
  if (!canAccessBlog) return <AccessDenied />;
  return (
    <div>
      <div className='flex justify-between'>
        <PageHeaderText title='Blog' description='Manage your article'>
          <Button
            onClick={goToCreateBlog}
            size='small'
            className='capitalize'
            color='primary'
            variant='block'>
            <AddIcon /> New Post
          </Button>
        </PageHeaderText>
      </div>
      <div className='flex justify-between items-center gap-x-6 mt-[-34px]  border-b border-gray-10/40 pb-6'>
        <ul className='flex gap-6 items-end h-full'>
          {blogFilters.map((status) => (
            <li
              onClick={() => setCareerPageStatus({ status: status.value.toLowerCase() })}
              key={status.value.toLowerCase()}
              className={`underline_animation_normal ${
                filterType === status.value.toLowerCase()
                  ? 'underline_animation_selected'
                  : 'underline_animation_notSelected'
              }`}>
              <button className='capitalize'>{status.name}</button>
            </li>
          ))}
        </ul>
      </div>

      <div>
        {loading ? (
          <div className='w-full flex items-center h-[50vh] justify-center'>
            <p> Loading</p>
          </div>
        ) : blogs.length === 0 ? (
          <div className='w-full flex justify-center h-[50vh] items-center'>
            <p>There are no blogs at the moment</p>
          </div>
        ) : (
          <InfiniteScroll
            dataLength={blogs.length}
            next={() => fetchMoreData()}
            hasMore={hasMore}
            height='75vh'
            loader={<h4>Loading...</h4>}
            endMessage={
              <p style={{ textAlign: 'center' }}>
                <b>Yay! You have seen it all</b>
              </p>
            }>
            <div className='grid md:grid-cols-3 gap-8 mt-10 '>
              {[...blogs]
                .sort((a, b) => new Date(b.updated_at).getTime() - new Date(a.updated_at).getTime())
                .map((blog, key) => (
                  <div key={key}>
                    <BlogCard refreshBlogs={updateBlogs} blog={blog} />
                  </div>
                ))}
            </div>
          </InfiniteScroll>
        )}
      </div>
    </div>
  );
};

export default Blogs;
